import type { MilkdownEditorHandle } from './editor';

import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface EditDocumentContextValue {
  /** Ref to the Milkdown editor instance */
  editorRef: React.RefObject<MilkdownEditorHandle | null>;
  /** Replace the entire document content (e.g. after AI edit) */
  setValue: (markdown: string) => void;
  /** Get current markdown from the editor */
  getMarkdown: () => string;
  /** Insert markdown at the end of the document (e.g. after image upload) */
  insertMarkdown: (markdown: string) => void;
}

const EditDocumentContext = createContext<EditDocumentContextValue | undefined>(
  undefined,
);

export interface EditDocumentProviderProps {
  editorRef: React.RefObject<MilkdownEditorHandle | null>;
  children: ReactNode;
}

export function EditDocumentProvider({
  editorRef,
  children,
}: EditDocumentProviderProps) {
  const value = useMemo<EditDocumentContextValue>(
    () => ({
      editorRef,
      setValue: (markdown: string) => {
        editorRef.current?.resetContent(markdown);
      },
      getMarkdown: () => editorRef.current?.getMarkdown() ?? '',
      insertMarkdown: (markdown: string) => {
        const current = editorRef.current?.getMarkdown() ?? '';
        const next = current ? `${current}\n\n${markdown}\n` : `${markdown}\n`;
        editorRef.current?.resetContent(next);
      },
    }),
    [editorRef],
  );

  return (
    <EditDocumentContext.Provider value={value}>
      {children}
    </EditDocumentContext.Provider>
  );
}

export function useEditDocument() {
  const context = useContext(EditDocumentContext);
  if (!context) {
    throw new Error('useEditDocument must be used within EditDocumentProvider');
  }
  return context;
}
